import { create } from 'zustand';
import { jobApi } from '@api/jobApi';
import { userManager } from '@providers/userManager';
import _ from 'lodash';
import { contentApi } from '@api/contentApi';
import axios from 'axios';

const CONSTANTS = {
    CACHE_DURATION: 5 * 60 * 1000, // 5 minutes cache
    RATE_LIMIT_RETRY_DELAY: 5000,
};

const useJobDescriptionsStore = create((set, get) => ({
    jobDescriptions: [],
    loading: false,
    error: null,
    lastFetched: null,
    actionLoadingMessage: {},

    // Add helper method to check cache validity
    isCacheValid: now => {
        const state = get();
        return (
            state.lastFetched &&
            now - state.lastFetched < CONSTANTS.CACHE_DURATION &&
            state.jobDescriptions.length > 0
        );
    },

    // Updated fetch with proper caching
    fetchJobDescriptions: async (force = false, loader = true) => {
        const now = Date.now();
        const state = get();

        if (!force && state.isCacheValid(now)) {
            return;
        }

        set({ loading: loader, error: null });

        try {
            const user = userManager.getUserData();
            if (!user?.id || !user.workEmail) {
                throw new Error('User data is missing');
            }

            const response = await jobApi.getJobDescriptions(user.id);

            // Normalize response to array
            const normalizedResponse = Array.isArray(response) ? response : [];

            // Handle nested array case
            const unwrappedResponse =
                normalizedResponse.length === 1 && Array.isArray(normalizedResponse[0])
                    ? normalizedResponse[0]
                    : normalizedResponse;

            // Filter for relevant items
            const filteredItems = unwrappedResponse.filter(item => item.jd_status);

            set({
                jobDescriptions: filteredItems,
                loading: false,
                lastFetched: now,
                error: null,
            });
        } catch (error) {
            set({
                error: `<strong>Status Code:</strong> ${
                    error.response?.status
                } <br/> <strong>Message:</strong> ${error.response?.data?.error || error.message}`,
                loading: false,
            });

            // Implement retry logic for specific errors
            if (error.response?.status === 429) {
                // Rate limit
                setTimeout(
                    () => get().fetchJobDescriptions(true),
                    CONSTANTS.RATE_LIMIT_RETRY_DELAY,
                );
            }
        }
    },

    // Optimized update logic
    updateJobDescriptions: newRequests => {
        if (!Array.isArray(newRequests)) return;

        set(state => {
            const updatedRequests = [...state.jobDescriptions];
            const updatedMap = new Map(updatedRequests.map(req => [req.ID, req]));

            newRequests.forEach(newReq => {
                if (!updatedMap.has(newReq.ID) || !_.isEqual(updatedMap.get(newReq.ID), newReq)) {
                    updatedMap.set(newReq.ID, newReq);
                }
            });

            return {
                jobDescriptions: Array.from(updatedMap.values()),
            };
        });
    },

    regenerateJobDescription: async (id, input, action, tablename) => {
        set({ loading: true, error: null });
        const messages = ['Regenerating JD...'];

        try {
            const user = userManager.getUserData();
            if (!user?.id) throw new Error('User authentication required');

            set({
                actionLoadingMessage: {
                    messages,
                    currentStep: 0,
                },
            });

            const payload = {
                user: {
                    id: user.id,
                    location: user.location,
                    email: user.workEmail,
                },
                job_id: id,
                query: input,
                action: action,
                table_name: tablename,
            };

            const response = await contentApi.generateJd(payload);

            if (!response?.result?.Job_Description) {
                throw new Error('Invalid response format');
            }

            // Create updated job description
            const updatedJobDescription = {
                ...get().jobDescriptions.find(jd => jd.ID === id),
                jd_Job_description: response.result.Job_Description,
            };

            // Update state
            set(state => ({
                jobDescriptions: state.jobDescriptions.map(jd =>
                    jd.ID === id ? updatedJobDescription : jd,
                ),
            }));

            set({ loading: false, actionLoadingMessage: {} });
            // Return the updated job description
            return updatedJobDescription;
        } catch (error) {
            set({
                error: `Failed to regenerate job description: ${error.message}`,
                loading: false,
            });
            throw error;
        } finally {
            set({ loading: false });
        }
    },

    // Add cleanup method
    clearStore: () => {
        set({
            jobDescriptions: [],
            loading: false,
            error: null,
            lastFetched: null,
        });
    },

    approveJobDescription: async jobDescription => {
        set({ loading: true, error: null });

        const messages = [
            'Approving JD...',
            'Sending email notification...',
            'Refreshing job descriptions...',
        ];

        try {
            const user = userManager.getUserData();
            if (!user?.id) throw new Error('User authentication required');

            const payload = {
                user: {
                    id: user.id,
                    location: user.location,
                    email: user.workEmail,
                },
                action: 'Approve',
                table_name: 'job_descriptions', // Adjust table name as needed
                comment: '--',
                req_id: jobDescription.ID,
                job_description:
                    jobDescription?.job_description || jobDescription?.jd_Job_description,
            };

            set({
                actionLoadingMessage: {
                    messages,
                    currentStep: 0,
                },
            });

            // Make the approval request
            await axios.post('/approve_jd', payload);

            set({
                actionLoadingMessage: {
                    messages,
                    currentStep: 1,
                },
            });

            // Send email notification
            await axios.post('/approve_jd_mail', {
                job_description:
                    jobDescription?.job_description || jobDescription?.jd_Job_description,
                table_name: jobDescription.table_name,
                req_id: jobDescription.ID,
                email: user.workEmail,
            });

            set({
                actionLoadingMessage: {
                    messages,
                    currentStep: 2,
                },
            });

            // Refresh the job descriptions list
            await get().fetchJobDescriptions(true);

            set({ loading: false, error: null, actionLoadingMessage: {} });
        } catch (error) {
            set({
                error: `Failed to approve job description: ${error.message}`,
                loading: false,
            });
            throw error;
        }
    },
}));

export default useJobDescriptionsStore;
